/**
 * Abrir e acompanhar ciclos.
 *
 * O QUE ESTA TELA FAZ DE DIFERENTE, e é o motivo de ela existir antes das
 * outras: ela mostra a conta da densidade ANTES de o peixe entrar.
 *
 * Da metade do ciclo em diante, quem decide quanto o peixe come nesta fazenda
 * não é o apetite dele — é o teto da água. A conta é simples e ninguém a fazia:
 *
 *     densidade de virada (t/ha) = teto (kg/ha/dia) ÷ %PV da faixa
 *
 * Acima dela, a água manda. E como a densidade sobe junto com o peixe, o
 * povoamento decide sozinho a partir de que peso o peixe vai comer menos do
 * que pediria. Hoje isso é consequência; esta tela transforma em escolha.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  abrirCiclo, listarCiclos, listarTanques, numeroDoParametro, parametrosDoCalculo,
  textoDoParametro, type Perfil,
  type Ciclo, type Fase, type ParametrosDoCalculo, type Tanque,
} from '../acesso/api'
import { diasEntre, pesoApos, type Curva } from '../dominio/crescimento'
import { tetoDoTanque } from '../dominio/agua'
import { porQueNaoResume, resumoDoLote, tratosPorDia } from '../dominio/lote'
import { Agrupamento, VoltarAoTopo } from './Agrupamento'
import {
  g, inteiro, kg, kgParaT, m2, m2ParaHa, numero, numeroPtBr,
  type Gramas, type Hectares,
} from '../dominio/unidades'
import { FechamentoDoLote } from './Fechamento'

const FASES: readonly Fase[] = ['alevinagem', 'juvenil', 'engorda', 'misto']
/** Peso de venda de referência. A fazenda fecha com mediana de 2,67 kg. */
const PESO_ALVO_PADRAO = 2500

/**
 * Erro mediano da curva no peso final, medido nos 89 trechos (Caderno 01).
 *
 * A tela mostra a FAIXA e não o número único por causa dele. Em 19/09/2026 a
 * curva deu 1.006 g para o T-4 e o peixeiro estimou 600 — erro de 40%, dentro
 * do que a própria curva avisa. Mostrar «1.006 g» convidaria alguém a confiar
 * num peso que ninguém pesou. É a regra 8: onde couber, a faixa em vez do
 * número.
 */
const ERRO_DA_CURVA = 0.34

export function Ciclos({ perfil }: { perfil: Perfil }) {
  const [ciclos, setCiclos] = useState<Ciclo[]>([])
  const [tanques, setTanques] = useState<Tanque[]>([])
  const [calculo, setCalculo] = useState<ParametrosDoCalculo | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [abrindo, setAbrindo] = useState(false)
  // UM FECHAMENTO ABERTO POR VEZ: cada um traz o seu papel, e dois na página
  // sairiam na mesma folha.
  const [fechamentoAberto, setFechamentoAberto] = useState<string | null>(null)

  const recarregar = useCallback(async () => {
    try {
      const [c, t, p] = await Promise.all([listarCiclos(), listarTanques(), parametrosDoCalculo()])
      setCiclos(c)
      setTanques(t)
      setCalculo(p)
      setErro(null)
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void recarregar()
  }, [recarregar])

  if (carregando) return <p className="aviso">Carregando…</p>

  const ocupados = new Set(ciclos.filter((c) => !c.encerrado).map((c) => c.tanque_id))

  // O ciclo não carrega o setor — quem tem setor é o TANQUE. A tela já carrega
  // os tanques para o formulário de abrir ciclo, então o mapa sai de graça.
  const setorDoTanque = new Map(tanques.map((t) => [t.id, t.setor]))
  const livres = tanques.filter((t) => t.ativo && !ocupados.has(t.id))
  const abertos = ciclos.filter((c) => !c.encerrado)

  return (
    <div className="ciclos">
      {erro && <p className="aviso-erro">{erro}</p>}

      <section className="bloco">
        <h2>Ciclos abertos</h2>

        {abertos.length === 0 ? (
          <p className="secundario">
            Nenhum ciclo aberto. Enquanto não houver peixe estocado, a ronda do
            tratador não tem o que mostrar.
          </p>
        ) : (
          <>
            {/* A LEGENDA, dita uma vez. Aqui a pergunta não é «já tratei» nem
                «já medi» — é «o que tem dentro desta represa». */}
            <p className="legenda-gaveta secundario">peixes · peso estimado</p>

            <Agrupamento
              tela="ciclos"
              itens={abertos.map((ciclo) => ({
                chave: ciclo.id,
                titulo: ciclo.tanque,
                setor: setorDoTanque.get(ciclo.tanque_id) ?? null,
                // NADA É PENDENTE NA LISTA DE CICLOS. Ciclo aberto não é
                // tarefa por fazer: ele é o estado da represa, e pintar todos
                // de «pendente» faria o contador do setor dizer sempre o
                // número de tanques — informação que a linha já dá ao lado.
                pendente: false,
                ciclo,
              }))}
              resumo={(i) => <ResumoDoCiclo ciclo={i.ciclo} />}
              ficha={(i) => (
                <ul className="lista">
                  <LinhaDeCiclo ciclo={i.ciclo} calculo={calculo} />
                </ul>
              )}
            />
            <VoltarAoTopo />
          </>
        )}

        {/* ABRIR CICLO É DO GESTOR. O tratador vê a lista — é dela que ele
            tira o que está estocado em cada tanque e o peso estimado —, mas
            povoar um tanque é decisão de quem compra alevino. O servidor já
            recusa; esconder o botão evita que ele descubra isso levando um
            «esta tela é do gestor» na cara depois de preencher o formulário. */}
        {perfil === 'tratador' ? null : abrindo ? (
          <NovoCiclo
            tanques={livres}
            calculo={calculo}
            aoFechar={() => setAbrindo(false)}
            aoAbrir={() => {
              setAbrindo(false)
              void recarregar()
            }}
          />
        ) : (
          <button
            type="button"
            className="discreto"
            onClick={() => setAbrindo(true)}
            disabled={livres.length === 0}
          >
            {livres.length === 0 ? 'Todos os tanques ativos estão ocupados' : '+ Abrir ciclo'}
          </button>
        )}
      </section>

      {ciclos.some((c) => c.encerrado) && (
        <section className="bloco">
          <h2>Encerrados</h2>
          <ul className="lista">
            {ciclos.filter((c) => c.encerrado).map((ciclo) => (
              <LinhaDeCiclo
                key={ciclo.id}
                ciclo={ciclo}
                calculo={calculo}
                fechamentoAberto={fechamentoAberto === ciclo.id}
                aoAlternarFechamento={() =>
                  setFechamentoAberto((atual) => (atual === ciclo.id ? null : ciclo.id))
                }
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

/**
 * Uma linha de ciclo, com o que o app SABE e o que ele ESTIMA, separados.
 *
 * O peso de hoje não foi medido — saiu da curva da casa a partir do povoamento.
 * A tela diz «estimado» com todas as letras, porque estimativa apresentada com
 * cara de número medido é a regra 8 sendo quebrada.
 */
/**
 * A LINHA FECHADA DO CICLO.
 *
 * «Peixes · peso estimado» — os dois números que respondem «o que tem dentro
 * desta represa». O peso vem em ITÁLICO porque é estimativa, e a regra 8 do
 * projeto manda que estimativa nunca tenha a mesma cara de número medido.
 */
function ResumoDoCiclo({ ciclo }: { ciclo: Ciclo }) {
  const peixes = peixesDoPovoamento(ciclo)
  const ancora = ciclo.peso_medido_g ?? ciclo.peso_medio_g
  return (
    <span className="gaveta-resumo-num">
      {peixes !== null ? inteiro(peixes) : '—'} ·{' '}
      {ancora !== null ? <em>{inteiro(g(ancora))} g</em> : '—'}
    </span>
  )
}

/**
 * A FICHA DO LOTE ATIVO — o pedido do gestor, por extenso.
 *
 *   «A1 10600 peixes, hoje estimado em 80 a 120 gramas, consumo nesta fase de
 *    1,2 a 1,6 % PV, previsão de consumo de Y kg de ração, recomendável N
 *    tratos por dia para esta fase. Biomassa Estimada 1,1 t hoje, Carga
 *    Represa 2,5 T/ha. Consumo acumulado até o momento 15.125 kg de ração
 *    desde início do ciclo»
 *
 * Duas coisas do jeito que ele pediu, e as duas são a regra 8:
 *
 *   — O CONSUMO É FAIXA, «1,2 a 1,6 % PV», e não a média das duas pontas. A
 *     tabela de arraçoamento dá um intervalo porque a resposta É um intervalo;
 *     escolher um número no meio esconderia isso de quem vai comprar ração.
 *
 *   — O CONSUMO ACUMULADO É O ÚNICO NÚMERO REDONDO DAQUI, junto com os peixes.
 *     Ele é soma de lançamentos: medido, não estimado. Por isso aparece
 *     separado dos outros, embaixo, e sem o «estimado».
 */
function FichaDoLote({
  ciclo, calculo, pesoHoje,
}: {
  ciclo: Ciclo
  calculo: ParametrosDoCalculo | null
  pesoHoje: number | null
}) {
  if (calculo === null) return null

  const lote = {
    peixes: ciclo.peixes_hoje,
    peso_hoje_g: pesoHoje,
    peso_min_g: null,
    peso_max_g: null,
    area_m2: ciclo.area_m2,
    tanque_id: ciclo.tanque_id,
    racao_acumulada_kg: ciclo.racao_acumulada_kg,
  }

  // O TETO É O DO MÊS DE HOJE. Esta ficha fala do presente — diferente do
  // Trato, que fala do dia escolhido no seletor e por isso usa o mês daquele.
  const teto = tetoDoTanque(calculo.tetos, ciclo.tanque_id, new Date().getMonth() + 1)
  const resumo = resumoDoLote(lote, calculo.arracoamento, teto)
  const ritmo = tratosPorDia(textoDoParametro(calculo.parametros, 'trato.por_dia'), ciclo.fase)

  if (resumo === null) {
    // A RAÇÃO ACUMULADA APARECE MESMO SEM O RESTO. Ela é medida e não depende
    // de curva nenhuma — esconder um número certo porque outro não fechou
    // seria perder o único que não tem dúvida.
    const razao = porQueNaoResume(lote, calculo.arracoamento)
    return (
      <div className="ficha-lote">
        {ciclo.racao_acumulada_kg > 0 && (
          <div className="ficha-medido">
            <strong>{inteiro(ciclo.racao_acumulada_kg)} kg</strong> de ração desde o início
          </div>
        )}
        <div className="secundario">
          {razao === 'sem_peixe' && 'Sem peixe vivo registrado neste ciclo.'}
          {razao === 'sem_peso' && 'Sem peso de entrada nem pesagem — não dá para estimar.'}
          {razao === 'fora_da_tabela' &&
            `Nenhuma faixa da tabela de arraçoamento cobre ${inteiro(pesoHoje ?? 0)} g.`}
        </div>
      </div>
    )
  }

  return (
    <div className="ficha-lote">
      <div className="ficha-linha">
        <span>come nesta fase</span>
        <span className="ficha-valor">
          {numero(resumo.faixa.pct_pv_min, 1)} a {numero(resumo.faixa.pct_pv_max, 1)}% do peso vivo
        </span>
      </div>

      <div className="ficha-linha">
        <span>previsão de hoje</span>
        {/* «69 a 69 kg» é o que sai quando o teto da água corta os dois lados
            da faixa — e faixa de um número só não é faixa, é um número com
            ruído em volta. Quando as pontas encontram, a tela diz uma. */}
        <span className="ficha-valor">
          {inteiro(resumo.previsto_kg.min) === inteiro(resumo.previsto_kg.max)
            ? `${inteiro(resumo.previsto_kg.max)} kg`
            : `${inteiro(resumo.previsto_kg.min)} a ${inteiro(resumo.previsto_kg.max)} kg`}
        </span>
      </div>

      {/* QUANDO A ÁGUA MANDA, A FICHA DIZ. Sem isto, quem comprasse ração pela
          previsão compraria para um consumo que a represa não aceita — e a
          diferença entre o que o peixe pediria e o que a água deixa é
          exatamente a «perda ambiental» que o projeto quer medir. */}
      {resumo.aguaManda && resumo.teto_kg !== null && (
        <div className="ficha-recado">
          A água limita: o peixe pediria até {inteiro(resumo.demanda_kg.max)} kg, e a represa
          aceita {inteiro(resumo.teto_kg)} kg por dia.
        </div>
      )}

      {ritmo !== null && (
        <div className="ficha-linha">
          <span>tratos por dia</span>
          <span className="ficha-valor">{ritmo}</span>
        </div>
      )}

      {/* MEDIDO, e por isso separado. Tudo acima pende da curva; isto é soma
          de lançamentos. */}
      <div className="ficha-medido">
        <strong>{inteiro(ciclo.racao_acumulada_kg)} kg</strong> de ração desde o início
      </div>
    </div>
  )
}

function LinhaDeCiclo({
  ciclo, calculo, fechamentoAberto = false, aoAlternarFechamento,
}: {
  ciclo: Ciclo
  calculo: ParametrosDoCalculo | null
  fechamentoAberto?: boolean
  aoAlternarFechamento?: () => void
}) {
  const peixes = peixesDoPovoamento(ciclo)
  const area = m2ParaHa(m2(ciclo.area_m2))
  // ENCERRADO CONTA ATÉ O ENCERRAMENTO, não até hoje: o peixe saiu da represa
  // naquele dia, e o lote não envelhece depois de vendido.
  const ate = ciclo.encerrado && ciclo.data_encerramento
    ? new Date(ciclo.data_encerramento).getTime()
    : Date.now()
  const dias = ciclo.data_povoamento
    ? Math.round((ate - new Date(ciclo.data_povoamento).getTime()) / 86_400_000)
    : null

  // A ÂNCORA É A ÚLTIMA PESAGEM, não o povoamento. O T-2 foi pesado na despesca
  // parcial de 20/08 — 2.555 g de média — e a tela estava extrapolando desde o
  // povoamento de 01/05, quatro meses antes, ignorando a balança para confiar
  // na conta. «Medido sempre ganha de calculado» é regra, e estava sendo
  // quebrada pela própria tela escrita para respeitá-la.
  const ancora = ciclo.peso_medido_g ?? ciclo.peso_medio_g
  const dataAncora = ciclo.data_medida ?? ciclo.data_povoamento
  const diasDesdeAncora =
    dataAncora !== null
      ? Math.round((Date.now() - new Date(dataAncora).getTime()) / 86_400_000)
      : null

  const curva =
    calculo !== null
      ? {
          a: numeroDoParametro(calculo.parametros, 'crescimento.curva.a', 0.135),
          b: numeroDoParametro(calculo.parametros, 'crescimento.curva.b', 0.00613),
          fator: numeroDoParametro(calculo.parametros, 'crescimento.fator_calibracao', 1),
        }
      : null

  const hoje =
    !ciclo.encerrado && curva && ancora && diasDesdeAncora !== null && diasDesdeAncora >= 0
      ? pesoApos(g(ancora), diasDesdeAncora, curva)
      : null

  // A INCERTEZA É DO GANHO, não do peso. O que a balança disse é conhecido; só
  // o que veio depois dela é estimado. Aplicar os 34% ao peso inteiro daria,
  // num tanque pesado há trinta dias, uma faixa de 2,2 a 4,5 kg — uma
  // amplitude que não corresponde a nada e que o gestor chamou, com razão, de
  // alucinação.
  const ganho = hoje !== null && ancora ? hoje - ancora : null
  const minimo = ganho !== null && ancora ? ancora + ganho * (1 - ERRO_DA_CURVA) : null
  const maximo = ganho !== null && ancora ? ancora + ganho * (1 + ERRO_DA_CURVA) : null

  const biomassa = hoje !== null && peixes !== null ? (peixes * hoje) / 1000 : null

  return (
    // O ENCERRADO FICA ESMAECIDO NA LISTA, mas não com o fechamento aberto: o
    // veredito do lote não pode chegar apagado, com 55% de contraste, no sol.
    <li className={ciclo.encerrado && !fechamentoAberto ? 'linha inativa' : 'linha'}>
      <div>
        <strong>{ciclo.codigo}</strong>
        <span className="etiqueta">{ciclo.tanque}</span>
        <span className="etiqueta">{ciclo.fase}</span>
        <div className="secundario">
          {peixes !== null && <>{inteiro(peixes)} peixes · </>}
          {ciclo.peso_medio_g !== null && <>entraram com {inteiro(ciclo.peso_medio_g)} g · </>}
          {numero(area, 2)} ha
          {dias !== null && <> · {dias} dias no tanque</>}
        </div>
        {hoje !== null && biomassa !== null && minimo !== null && maximo !== null && (
          <div className="estimativa">
            {ciclo.peso_medido_g !== null && dataAncora !== null && (
              <div className="estimativa-ancora">
                pesado {quando(dataAncora)}: <strong>{inteiro(ciclo.peso_medido_g)} g</strong>
                {ciclo.fonte && <> · {ciclo.fonte.replace(/_/g, ' ')}</>}
              </div>
            )}
            hoje, <em>estimado</em>: <strong>{inteiro(minimo)} a {inteiro(maximo)} g</strong>
            {' · '}{numero(biomassa / 1000, 1)} t · {numero(biomassa / 1000 / area, 2)} t/ha
            <div className="estimativa-nota">
              {diasDesdeAncora !== null && diasDesdeAncora <= 1
                ? 'pesado hoje — sem estimativa no meio.'
                : `${diasDesdeAncora} dias de estimativa desde a última pesagem. `}
              {diasDesdeAncora !== null && diasDesdeAncora > 60 && (
                <em>Faz tempo. Só a balança responde quanto este tanque pesa.</em>
              )}
            </div>
          </div>
        )}

        {!ciclo.encerrado && (
          <FichaDoLote ciclo={ciclo} calculo={calculo} pesoHoje={hoje} />
        )}

        {/* O FECHAMENTO — o veredito do lote (docs/FECHAMENTO.md). */}
        {ciclo.encerrado && aoAlternarFechamento && (
          <button type="button" className="discreto" onClick={aoAlternarFechamento}>
            {fechamentoAberto ? 'fechar o fechamento' : 'ver o fechamento'}
          </button>
        )}
        {ciclo.encerrado && fechamentoAberto && <FechamentoDoLote cicloId={ciclo.id} />}
      </div>
    </li>
  )
}

function NovoCiclo({
  tanques, calculo, aoFechar, aoAbrir,
}: {
  tanques: Tanque[]
  calculo: ParametrosDoCalculo | null
  aoFechar: () => void
  aoAbrir: () => void
}) {
  const [tanqueId, setTanqueId] = useState(tanques[0]?.id ?? '')
  const [fase, setFase] = useState<Fase>('engorda')
  const [dataInicio, setDataInicio] = useState(hoje())
  const [modo, setModo] = useState<'contagem' | 'pesagem'>('contagem')
  const [quantidade, setQuantidade] = useState('')
  const [pesoTotal, setPesoTotal] = useState('')
  const [pesoMedio, setPesoMedio] = useState('')
  const [pesoAlvo, setPesoAlvo] = useState(String(PESO_ALVO_PADRAO))
  const [observacao, setObservacao] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const tanque = tanques.find((t) => t.id === tanqueId) ?? null
  // Padrão do sistema: vírgula decimal, ponto de milhar. «4.300» alevinos é
  // quatro mil e trezentos, e não 4,3 — que era o que saía antes.
  const nQuantidade = numeroPtBr(quantidade) ?? NaN
  const nPesoTotal = numeroPtBr(pesoTotal) ?? NaN
  const nPesoMedio = numeroPtBr(pesoMedio) ?? NaN
  const nPesoAlvo = numeroPtBr(pesoAlvo) ?? NaN

  const peixes =
    modo === 'contagem'
      ? nQuantidade
      : Number.isFinite(nPesoTotal) && Number.isFinite(nPesoMedio) && nPesoMedio > 0
        ? (nPesoTotal * 1000) / nPesoMedio
        : NaN

  return (
    <form
      className="nova-pessoa"
      onSubmit={(evento) => {
        evento.preventDefault()
        setSalvando(true)
        setErro(null)
        abrirCiclo({
          tanque_id: tanqueId,
          fase,
          data_inicio: dataInicio,
          modo_apuracao: modo,
          quantidade: modo === 'contagem' ? Math.round(nQuantidade) : null,
          peso_total_kg: modo === 'pesagem' ? nPesoTotal : null,
          peso_medio_g: nPesoMedio,
          observacao: observacao.trim() === '' ? null : observacao.trim(),
        })
          .then(aoAbrir)
          .catch((falha: unknown) => {
            setErro(falha instanceof Error ? falha.message : 'não deu para abrir')
          })
          .finally(() => setSalvando(false))
      }}
    >
      <label className="campo">
        <span className="campo-rotulo">Tanque</span>
        <select value={tanqueId} onChange={(e) => setTanqueId(e.target.value)}>
          {tanques.map((t) => (
            <option key={t.id} value={t.id}>
              {t.codigo} — {numero(m2ParaHa(m2(t.area_m2)), 2)} ha
              {t.tipo === 'represa' ? ' (represa fonte)' : ''}
            </option>
          ))}
        </select>
      </label>

      <label className="campo">
        <span className="campo-rotulo">Fase</span>
        <select value={fase} onChange={(e) => setFase(e.target.value as Fase)}>
          {FASES.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </label>

      <label className="campo">
        <span className="campo-rotulo">Data de entrada do peixe</span>
        <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
      </label>

      <label className="campo">
        <span className="campo-rotulo">O peixe foi contado ou pesado?</span>
        <select value={modo} onChange={(e) => setModo(e.target.value as 'contagem' | 'pesagem')}>
          <option value="contagem">contado — sei quantos peixes entraram</option>
          <option value="pesagem">pesado — sei quantos quilos entraram</option>
        </select>
      </label>

      {modo === 'contagem' ? (
        <label className="campo">
          <span className="campo-rotulo">Quantos peixes</span>
          <input
            value={quantidade}
            onChange={(e) => setQuantidade(e.target.value)}
            inputMode="numeric"
            placeholder="24000"
          />
        </label>
      ) : (
        <label className="campo">
          <span className="campo-rotulo">Quantos quilos entraram</span>
          <input
            value={pesoTotal}
            onChange={(e) => setPesoTotal(e.target.value)}
            inputMode="decimal"
            placeholder="2650"
          />
        </label>
      )}

      <label className="campo">
        <span className="campo-rotulo">Peso médio de entrada, em gramas</span>
        <input
          value={pesoMedio}
          onChange={(e) => setPesoMedio(e.target.value)}
          inputMode="decimal"
          placeholder="280"
        />
        {/* O histórico é duro com entrada leve: sobrevivência mediana de 0,87
            abaixo de 250 g contra 0,98 acima de 400 g. Avisar aqui é barato;
            descobrir no fechamento, não. */}
        {Number.isFinite(nPesoMedio) && nPesoMedio > 0 && nPesoMedio < 250 && (
          <span className="aviso-erro">
            Abaixo de 250 g a predação pesa: o histórico da fazenda mostra
            sobrevivência mediana de 87%, contra 98% acima de 400 g.
          </span>
        )}
      </label>

      <label className="campo">
        <span className="campo-rotulo">Peso de despesca pretendido, em gramas</span>
        <input
          value={pesoAlvo}
          onChange={(e) => setPesoAlvo(e.target.value)}
          inputMode="decimal"
        />
        <span className="secundario">
          Não é gravado — serve só para a projeção abaixo.
        </span>
      </label>

      {tanque && calculo && Number.isFinite(peixes) && peixes > 0 &&
        Number.isFinite(nPesoMedio) && nPesoMedio > 0 &&
        Number.isFinite(nPesoAlvo) && nPesoAlvo > nPesoMedio && (
          <Projecao
            tanque={tanque}
            peixes={peixes}
            pesoEntrada={g(nPesoMedio)}
            pesoAlvo={g(nPesoAlvo)}
            calculo={calculo}
          />
        )}

      <label className="campo">
        <span className="campo-rotulo">Observação (opcional)</span>
        <input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
      </label>

      {erro && <p className="aviso-erro">{erro}</p>}

      <div className="acoes">
        <button type="submit" disabled={salvando}>
          {salvando ? 'Abrindo…' : 'Abrir ciclo'}
        </button>
        <button type="button" className="discreto" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </form>
  )
}

/**
 * A conta que faltava: a partir de que peso a água passa a mandar.
 *
 * Ela não impede nada e não recomenda nada. Mostra o que a decisão de
 * povoamento já implica, enquanto ainda dá para mudar o número de peixes.
 */
function Projecao({
  tanque, peixes, pesoEntrada, pesoAlvo, calculo,
}: {
  tanque: Tanque
  peixes: number
  pesoEntrada: Gramas
  pesoAlvo: Gramas
  calculo: ParametrosDoCalculo
}) {
  const area = m2ParaHa(m2(tanque.area_m2))

  const curva: Curva = useMemo(
    () => ({
      a: numeroDoParametro(calculo.parametros, 'crescimento.curva.a', 0.135),
      b: numeroDoParametro(calculo.parametros, 'crescimento.curva.b', 0.00613),
      fator: numeroDoParametro(calculo.parametros, 'crescimento.fator_calibracao', 1),
    }),
    [calculo.parametros],
  )

  // O teto da SECA, que é o mais apertado dos dois. Um ciclo de 286 dias
  // atravessa as duas estações; planejar pelo mais folgado é planejar para a
  // metade boa do ano.
  const tetoSeca = useMemo(() => {
    const doTanque = calculo.tetos.find((t) => t.tanque_id === tanque.id && t.mes_inicio === 5)
    const geral = calculo.tetos.find((t) => t.tanque_id === null && t.mes_inicio === 5)
    return doTanque?.kg_ha_dia ?? geral?.kg_ha_dia ?? null
  }, [calculo.tetos, tanque.id])

  const dias = diasEntre(pesoEntrada, pesoAlvo, curva)
  const densidadeFinal = densidade(peixes, pesoAlvo, area)
  const alvo = tanque.carga_final_alvo_t_ha

  // Marcos ao longo do ciclo, só os que ficam entre a entrada e o alvo.
  const marcos = [pesoEntrada as number, 700, 1200, 1800, pesoAlvo as number]
    .filter((p, i, todos) => p >= pesoEntrada && p <= pesoAlvo && todos.indexOf(p) === i)
    .sort((x, y) => x - y)

  return (
    <div className="projecao">
      <h3>O que este povoamento implica</h3>

      <div className="linhas">
        <div>
          <dt>Biomassa de entrada</dt>
          <dd>{numero(kgParaT(kg((peixes * pesoEntrada) / 1000)), 2)} t</dd>
        </div>
        <div>
          <dt>Tempo até {inteiro(pesoAlvo)} g, pela curva da casa</dt>
          <dd>{Number.isFinite(dias) ? `${inteiro(dias)} dias` : '—'}</dd>
        </div>
        <div className={alvo !== null && densidadeFinal > alvo ? 'destaque' : undefined}>
          <dt>Densidade na despesca</dt>
          <dd>
            {numero(densidadeFinal, 2)} t/ha
            {alvo !== null && <> · alvo do tanque {numero(alvo, 1)}</>}
          </dd>
        </div>
      </div>

      {alvo !== null && densidadeFinal > alvo && (
        <p className="aviso-erro">
          Passa do alvo deste tanque. Em represa fonte isso não custa só a ela:
          a água que renova os outros deixa de renovar.
        </p>
      )}

      {tetoSeca !== null && (
        <>
          <p className="secundario">
            Quem manda ao longo do ciclo, com o teto da seca de {inteiro(tetoSeca)} kg/ha/dia:
          </p>
          <table className="projecao-tabela">
            <thead>
              <tr><th>Peso</th><th>Densidade</th><th>Quem manda</th></tr>
            </thead>
            <tbody>
              {marcos.map((peso) => {
                const faixa = calculo.arracoamento.find(
                  (f) => peso >= f.peso_de_g && peso < f.peso_ate_g,
                )
                const d = densidade(peixes, g(peso), area)
                const virada = faixa ? tetoSeca / (faixa.pct_pv_max / 100) / 1000 : null
                const aguaManda = virada !== null && d > virada
                return (
                  <tr key={peso}>
                    <td>{inteiro(peso)} g</td>
                    <td>{numero(d, 2)} t/ha</td>
                    <td className={aguaManda ? 'manda-agua' : undefined}>
                      {virada === null ? '—' : aguaManda ? 'a água' : 'o peixe'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <p className="secundario">
            «A água manda» quer dizer que o peixe vai comer menos do que pediria.
            Não é defeito — é o preço da densidade escolhida, e ele é decidido
            agora, não no meio do ciclo.
          </p>
        </>
      )}
    </div>
  )
}

function densidade(peixes: number, peso: Gramas, area: Hectares): number {
  return (peixes * peso) / 1_000_000 / area
}

function peixesDoPovoamento(ciclo: Ciclo): number | null {
  if (ciclo.quantidade !== null) return ciclo.quantidade
  if (ciclo.peso_total_kg !== null && ciclo.peso_medio_g) {
    return (ciclo.peso_total_kg * 1000) / ciclo.peso_medio_g
  }
  return null
}

/** «há 30 dias» diz mais que «20/08/2026» quando o assunto é confiança. */
function quando(iso: string): string {
  const data = new Date(iso)
  const dias = Math.round((Date.now() - data.getTime()) / 86_400_000)
  if (dias <= 0) return 'hoje'
  if (dias === 1) return 'ontem'
  if (dias < 60) return `há ${dias} dias`
  return `em ${data.toLocaleDateString('pt-BR')}`
}

function hoje(): string {
  const agora = new Date()
  const mes = String(agora.getMonth() + 1).padStart(2, '0')
  const dia = String(agora.getDate()).padStart(2, '0')
  return `${agora.getFullYear()}-${mes}-${dia}`
}
